import intersect from 'path-intersection'
import * as shape from 'd3-shape'
import svgpath from 'svgpath'
import {
  calculateSubcurve,
  extractPoints,
  reversePath,
  shortenCurve,
  splitBezierPath,
} from 'silkpath'

/*
 * Calculates the center coordinate of the node inside the wob
 * within the canvas. We can't just use wob.center() b/c the connector container
 * doesn't necessarily occupy 100% width/height of the wob
 */
const getCenter = function (wob, widget) {
  if (widget.connectorContainerCenter) {
    return widget.connectorContainerCenter(wob)
  }

  const node = widget.connectorContainer()
  const bb = node.getBBox()

  // Check if the node has a data defining its center
  const dimensions = node.dataset.dimensions ? JSON.parse(node.dataset.dimensions) : {
    width: bb.width,
    height: bb.height
  }

  const left = wob.get('left')
  const top = wob.get('top')

  // TODO at some point we might realize we need a `+ bb.x` and `+ bb.y` here
  return [
    left + (dimensions.width / 2),
    top + (dimensions.height / 2)
  ]
}

/*
 * Calculates the distance between two points
 */
const distance = function (p1, p2) {
  return Math.sqrt(Math.pow((p1[0] - p2[0]), 2) + Math.pow((p1[1] - p2[1]), 2))
}

const generator = shape.line()
                       .curve(shape.curveNatural) // This curves gives us a curve that matches keynote

export default function (connector, canvas) {
  // The from wob is just the collection's from
  var fromCenter = getCenter(connector.collection.from, canvas.views[connector.collection.from.cid].view)
    , toCenter = null

  if (connector.get('to')) {
    // Locked to a wob, use the widget's connector container center
    toCenter = getCenter(connector.get('to'), canvas.views[connector.get('to').cid].view)
  } else if (connector.get('toCenter')) {
    // They are moving the head. We know it's the head control b/c we don't show a midpoint control, unless
    // there is a link between wobs (i.e. we have a `to`)
    toCenter = connector.get('toCenter')
    connector.set('toCenter', null, {silent: true})
  } else {
    var xOffset = 35
      , yOffset = 75
    // Not attached and not moving the head. Space it away from the wob
    // All new arrows should go up 75px and to the right 35 px from the top center of the widget.
    toCenter = [fromCenter[0] + xOffset, connector.collection.from.get('top') - yOffset]

    if (toCenter[1] < 0) {
      // If that point is off the canvas to the top, draw the arrow down 75px and to the right 35px from the bottom center of the widget.
      toCenter[1] = (connector.collection.from.get('top') + connector.collection.from.get('height')) + yOffset
    }
  }

  if (!connector.get('control')) {
    // This is a an arrow that isn't attached yet. Throw it at the midpoint between the lines
    connector.set('control', [(fromCenter[0] + toCenter[0]) / 2, (fromCenter[1] + toCenter[1]) / 2], {silent: true})
  }

  if (!connector.get('midpointPercentage')) {
    // We have to calculate the midpoint height and percentage along the path
    // So we can keep those angles if the wobs are moved or the HEAD of the path is adjusted

    // Use the law of cosines (https://en.wikipedia.org/wiki/Law_of_cosines) to calculate the angle, theta, of the vector from
    // the center of the `from` wob to the midpoint control, and the vector from the `from` wob to the `to` wob
    var p12 = distance(fromCenter, connector.get('control')) // `from` wob to midpoint length
      , p13 = distance(fromCenter, toCenter) // `from` wob to `to` wob length
      , dAx = connector.get('control')[0] - fromCenter[0]
      , dAy = connector.get('control')[1] - fromCenter[1]
      , dBx = toCenter[0] - fromCenter[0]
      , dBy = toCenter[1] - fromCenter[1]
      , theta = Math.atan2(dAx * dBy - dAy * dBx, dAx * dBx + dAy * dBy)

    connector.set('midpointHeight', Math.sin(theta) * p12, {silent: true})
    connector.set('midpointPercentage', (Math.cos(theta) * p12) / p13, {silent: true})// percentage the adjacent of the entire line from `from` to `to,`
  } else {
    // Calculate a new control
    // A connector head/tail is changing, we need to calculate the new midpoint
    var p13 = distance(fromCenter, toCenter) // `from` wob to `to` wob length
      , opposite = connector.get('midpointHeight')
      , adjacent = p13 * connector.get('midpointPercentage')
      , hypotenuse = Math.sqrt(Math.pow(adjacent, 2) + Math.pow(opposite,2))
      , theta = Math.asin(Math.abs(opposite) * Math.sin(90 * Math.PI / 180) / hypotenuse) // Angle between the vector cast from `from` to `to, and the vector from `from` to the midpoint
      , phi = Math.atan2((toCenter[1] - fromCenter[1]), toCenter[0] - fromCenter[0]) // Angle of the the line from `from` to `to`

    // By default our calculation assumes the point is in quadrant 1, but depending
    // on the signs of adjacent and opposite, we might need to adjust theta
    if (adjacent < 0 && opposite > 0) {
      // Move into quadrant II
      theta = Math.PI - theta
    }

    if (adjacent < 0 && opposite < 0) {
      // Move into quadrant III
      theta = -(Math.PI - theta)
    }

    if (adjacent > 0 && opposite < 0) {
      // Move into quadrant V
      theta = -theta
    }

    connector.set('control', [ fromCenter[0] + Math.cos(theta - phi) * hypotenuse, fromCenter[1] - Math.sin(theta - phi) * hypotenuse ], {silent: true})
  }

  const curves = splitBezierPath(generator([fromCenter, connector.get('control'), toCenter]))
  const arrowWidth = parseInt(connector.get('style')['stroke-width'], 10) + 2

  const from = getConnection(reversePath(curves[0]), canvas.views[connector.collection.from.cid].view.connectorContainer(), {
    center: fromCenter,
    rotate: connector.collection.from.get('rotate'),
    offset: {
      left: connector.collection.from.get('left'),
      top: connector.collection.from.get('top')
    },
    control: connector.get('control')
  })

  let to = null
  if (connector.get('to')) {
    to = getConnection(curves[1], canvas.views[connector.get('to').cid].view.connectorContainer(), {
      center: toCenter,
      rotate: connector.get('to').get('rotate'),
      offset: {
        left: connector.get('to').get('left'),
        top: connector.get('to').get('top')
      },
      control: connector.get('control')
    }, arrowWidth * 2)
  } else {
    const curve = extractPoints(curves[1])
    const t = shortenCurve(curve, arrowWidth * 2)
    const subCurveShortened = calculateSubcurve(t, ...curve)
    to = {
      center: toCenter,
      intersection: [
        subCurveShortened.start,
        [toCenter[0], toCenter[1]],
      ],
      path: `M${curve[0]} C${curve[1]} ${curve[2]} ${curve[3]}`,
    }
  }

  return {
    control: connector.get('control'),
    from,
    to,
    connector,
    arrowWidth,
    intersectionOffset: from.intersection.x ? fromCenter[1] - from.intersection.y : null,
    path: `${from.path} ${to.path}`,
  }
}

function getConnection (curve, path, pos, offset = 0) {
  // path comes in as node

  // extract translate values defined on the path
  const translateRegex = /translate\(([^,]+),\s*([^)]+)\)/
  const match = translateRegex.exec(path.getAttribute('transform'))
  const transform = {x: 0, y: 0} // default to 0, 0

  if (match) {
    // The node has a translate, so we need to offset the path by that amount
    transform.x = parseFloat(match[1])
    transform.y = parseFloat(match[2])
  }

  path = svgpath(path.getAttribute('d'))
    .translate(pos.offset.left + transform.x, pos.offset.top + transform.y)
    .rotate(pos.rotate, pos.center[0], pos.center[1])
    .toString()

  const intersections = intersect(curve, path)
  const control = pos.control

  const intersection = intersections.sort((a, b) => {
    const d1 = Math.sqrt((a.x - control[0]) ** 2 + (a.y - control[1]) ** 2)
    const d2 = Math.sqrt((b.x - control[0]) ** 2 + (b.y - control[1]) ** 2)
    return d1 - d2
  }).at(0) || {
    // if we don't have a position, use the center
    x: pos.center[0], y: pos.center[1]
  }

  const points = intersection.bez1?.reduce((acc, val, i) => (i % 2 === 0 ? acc.push([val]) : acc[acc.length - 1].push(val), acc), []) || extractPoints(curve)
  const subCurve = calculateSubcurve(1 - (intersection.t1 || 1), ...points.reverse())
  const _intersection = [intersection.x, intersection.y]

  const result = [
    control,
    subCurve.controls[1],
    subCurve.controls[0],
    subCurve.start
  ]

  let shortened = null
  if (offset) {
    const t = shortenCurve(result, offset)
    const subCurveShortened = calculateSubcurve(1 - t, ...result.slice(0).reverse())

    shortened = {
      intersection: subCurveShortened.start,
    }
    result[1] = subCurveShortened.controls[1]
    result[2] = subCurveShortened.controls[0]
    result[3] = subCurveShortened.start

    _intersection[0] = subCurveShortened.start
    _intersection[1] = [intersection.x, intersection.y]
  }

  return {
    path: `M${result[0]} C${result[1]}, ${result[2]}, ${result[3]}`,
    intersection: _intersection,
    shortened,
    center: pos.center,
  }
}
